import { StackProps, Typography } from '@mui/material';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import Iconify from 'src/components/iconify';
import { VoiceChannelMember } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { VoiceChannelMemberGridItem } from './VoiceMemberGridItem';
import { MAX_USERS } from './constants';
import {
  CollapseListButton,
  ContainerStyled,
  GridContainerStyled,
  StyledMoreUsers,
} from './styles';
import { useVoiceChannelMembersGrid } from './useVoiceChannelMembersGrid';

type Props = StackProps & {
  conversationId: string;
  participants: VoiceChannelMember[];
  isGuest?: boolean;
};

export const VoiceChannelMembersGrid = ({
  conversationId,
  participants,
  isGuest = false,
}: Props) => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const {
    setTheaterMember,
    setActiveMember,
    theaterMember,
    activeMember,
    viewMode,
  } = useLivekitRTCState();

  const {
    rows,
    cols,
    activeMemberOrSomeone,
    connected,
    listView,
    listViewActiveParticipant,
    participantsWithoutMe,
    visibleUsers,
    extraUsers,
  } = useVoiceChannelMembersGrid({ conversationId, participants });

  useEffect(() => {
    if (viewMode === 'grid' || !!activeMember) return;
    setActiveMember(activeMemberOrSomeone as string);
  }, [activeMemberOrSomeone, activeMember, viewMode, setActiveMember]);

  useEffect(() => {
    if (theaterMember || viewMode === 'list') return;
    setCollapsed(false);
  }, [theaterMember, viewMode]);

  return (
    <ContainerStyled className={cn({ connected, listView, isGuest })}>
      {listView && (
        <>
          <VoiceChannelMemberGridItem
            member={listViewActiveParticipant as VoiceChannelMember}
            connected={connected}
          />
          {!!participantsWithoutMe.length && (
            <CollapseListButton
              className={cn({ collapsed })}
              size="small"
              onClick={() => setCollapsed((state) => !state)}
            >
              <Iconify
                icon={
                  collapsed ? 'tabler:chevron-left' : 'tabler:chevron-right'
                }
                width={16}
                height={16}
              />
            </CollapseListButton>
          )}
        </>
      )}
      <GridContainerStyled
        sx={(theme) => ({
          '--columns': 1,
          '--rows': Math.max(5, visibleUsers.length),
          [theme.breakpoints.up('sm')]: {
            '--columns': cols,
            '--rows': rows,
          },
          display:
            collapsed || (listView && participants.length === 1)
              ? 'none'
              : 'flex',
        })}
        className={cn({ connected, listView, isGuest })}
      >
        {visibleUsers.map((member) => (
          <VoiceChannelMemberGridItem
            key={member.user._id}
            onClick={() =>
              listView && setTheaterMember(member.meetId as string)
            }
            member={member}
            connected={connected}
            aloneMember={participants?.length === 1}
          />
        ))}
        {!listView && participants.length > MAX_USERS && (
          <StyledMoreUsers>
            <Typography
              variant="h2"
              color={(theme) => theme.palette.common.white}
            >
              +{extraUsers}
            </Typography>
            <Typography variant="caption" whiteSpace="nowrap">
              More Users
            </Typography>
          </StyledMoreUsers>
        )}
      </GridContainerStyled>
    </ContainerStyled>
  );
};
